import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { length, point, shortestPath } from '@turf/turf';
import { CodaltComponent } from 'app/codalt.component';
import { switchMap, tap } from 'rxjs/operators';
import { Customer } from '../../customer';
import { CustomerProject } from '../../customer-project.interface';
import { Filter } from '../../filter';
import { PdfStyle } from '../../pdf-style';
import { PdfStyleService } from '../../pdf-style.service';
import { AuthorisationService } from '../../services/auth/authorisation.service';
import { Functionalities } from '../../services/auth/functionalities';
import { CustomerProjectService } from '../../services/customer-project/customer-project.service';
import { CustomerService } from '../../services/customer/customer.service';
import { WorkareaService } from '../../services/workarea/workarea.service';
import { Workarea } from '../../workarea.class';
export interface ExportDialogData {
    project?: CustomerProject;
    customer?: Customer;
    type: '' | 'Pdf' | 'Excel';
    initialWorkareaIds?: number[];
}

@Component({
    selector: 'app-customer-project-export-dialog',
    templateUrl: './customer-project-export-dialog.component.html',
    styleUrls: ['./customer-project-export-dialog.component.scss'],
    standalone: false,
})
export class CustomerProjectExportDialogComponent extends CodaltComponent implements OnInit {
    project: CustomerProject;
    availableWorkareas: Workarea[] = [];
    availablePhaseDocuments: {
        id: number;
        label: string;
    }[] = [];

    form!: FormGroup;
    statusCounts: {
        name: string;
        count: number;
        colorcode: string;
        id: number;
    }[] = [];

    filteredWorkareas: Workarea[] = [];
    availableReports: string[] = [];
    pdfStyles: PdfStyle[] = [];
    manuallySelectedIds: number[] = [];

    constructor(
        private customerProjectService: CustomerProjectService,
        private workareaService: WorkareaService,
        private pdfStyleService: PdfStyleService,
        private customerService: CustomerService,
        private dialog: MatDialogRef<CustomerProjectExportDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: ExportDialogData
    ) {
        super();
        if (!data.project) {
            throw new Error('CustomerProjectExportDialogComponent requires data.project');
        }
        this.project = data.project;
    }

    ngOnInit(): void {
        this.form = new FormGroup({
            projectId: new FormControl(this.project.id),
            workarea_per_page: new FormControl(false),
            include_workarea_table: new FormControl(true),
            map_type: new FormControl('map'),
            workarea_sort: new FormControl('number'),
            pdf_style_key: new FormControl(null),
            workarea_ids: new FormControl([]),
            phase_document_reply_ids: new FormControl([]),
            report_files: new FormControl([]),
        });

        this.subscriptions.add(
            this.form.controls.workarea_ids.valueChanges.subscribe((selectedIds: number[] = []) => {
                const sortMode = this.form.controls.workarea_sort.value;
                // Always track user-driven selection changes (programmatic changes use emitEvent:false)
                this.manuallySelectedIds = [...selectedIds];
                if (sortMode === 'distance' && selectedIds.length > 0) {
                    // In distance mode, re-sort with new first item as anchor
                    const resortedIds = this.sortWorkareaIds(selectedIds, 'distance');
                    if (JSON.stringify(resortedIds) !== JSON.stringify(selectedIds)) {
                        this.form.controls.workarea_ids.setValue(resortedIds, { emitEvent: false });
                        this.manuallySelectedIds = [...resortedIds];
                    }
                }
            })
        );

        this.subscriptions.add(
            this.form.controls.workarea_sort.valueChanges.subscribe((sortMode: string) =>
                this.updateWorkareaIdsBySortMode(sortMode)
            )
        );

        this.availableReports = this.data.project?.extra?.reports ?? [];

        const phases = this.project?.customer_project_phases ?? [];
        this.availablePhaseDocuments = phases.flatMap(phase => {
            const activitiesById = new Map(
                (phase.customer_project_phase_activities ?? []).map(activity => [activity.id, activity])
            );
            return (phase.customer_project_phase_replies ?? [])
                .filter(reply => reply.is_document && (reply.text || '').toLowerCase().endsWith('.pdf'))
                .map(reply => {
                    const activity = activitiesById.get(reply.customer_projectphase_activity_id);
                    const replyName = reply.name || reply.text;
                    const activityPart = activity?.name ? ` / ${activity.name}` : '';

                    return {
                        id: reply.id,
                        label: `${phase.name}${activityPart} - ${replyName}`,
                    };
                });
        });

        if (this.availablePhaseDocuments.length == 0) {
            this.form.controls.phase_document_reply_ids.disable();
        }

        const projectFilter = new Filter();
        projectFilter.project = this.project.id;

        this.subscriptions.add(
            this.workareaService.get({ customer_project_id: this.project.id }).subscribe({
                next: workareas => {
                    console.log(
                        `Loaded ${workareas.data.length} workareas for project ${this.project.id}`,
                        workareas.data
                    );
                    this.availableWorkareas = workareas.data;
                    this.updateFilteredWorkareas();

                    if (this.data.initialWorkareaIds?.length) {
                        const validIds = this.data.initialWorkareaIds.filter(id =>
                            this.availableWorkareas.some(w => w.id === id)
                        );
                        this.manuallySelectedIds = [...validIds];
                        this.form.controls.workarea_ids.setValue(validIds, { emitEvent: false });
                    }
                },
            })
        );

        this.subscriptions.add(
            this.customerProjectService
                .getProjectsWorkareaCount(this.project.id)
                .pipe(
                    tap(counts => {
                        this.statusCounts = counts;
                        const countsWithWorkareas = counts.filter(s => s.count > 0);
                        const autoSelect = countsWithWorkareas.length === 1 ? countsWithWorkareas[0].id : null;
                        const statusGroup = new FormGroup(
                            counts.reduce(
                                (controls, status) => ({
                                    ...controls,
                                    [status.id]: new FormControl(status.id === autoSelect ? true : null),
                                }),
                                {}
                            )
                        );
                        this.form.addControl('status', statusGroup);
                    }),
                    switchMap(() => (this.form.controls.status as FormGroup).valueChanges)
                )
                .subscribe({ next: () => this.updateFilteredWorkareas() })
        );

        if (AuthorisationService.hasFunctionality(Functionalities.GeoManage)) {
            this.subscriptions.add(
                this.pdfStyleService.getStyles().subscribe({
                    next: styles => {
                        this.pdfStyles = styles.sort((left, right) => {
                            if (left.is_default !== right.is_default) {
                                return Number(right.is_default) - Number(left.is_default);
                            }
                            return left.name.localeCompare(right.name);
                        });

                        const customerDefault = this.data.customer?.default_pdf_style_key;
                        const defaultKey = customerDefault
                            ? this.pdfStyles.find(s => s.key === customerDefault)?.key
                            : this.pdfStyles.find(s => s.is_default)?.key;
                        this.form.controls.pdf_style_key.setValue(defaultKey || null);
                    },
                })
            );
        }
    }

    private updateFilteredWorkareas() {
        const statusGroup = this.form?.controls?.status as FormGroup;
        if (!statusGroup) {
            this.filteredWorkareas = this.availableWorkareas;
            return;
        }
        const selectedIds = Object.entries(statusGroup.value)
            .filter(([, selected]) => !!selected)
            .map(([id]) => +id);

        this.filteredWorkareas =
            selectedIds.length === 0
                ? this.availableWorkareas
                : this.availableWorkareas.filter(w => selectedIds.includes(w.status_id));

        // Re-apply sorting after filtered workareas change
        if (this.form) {
            this.updateWorkareaIdsBySortMode(this.form.controls.workarea_sort.value);
        }
    }

    private updateWorkareaIdsBySortMode(sortMode: string) {
        if (!this.form || !this.filteredWorkareas.length) {
            return;
        }

        const sortMode_ = sortMode || 'number';
        const filteredIds = this.filteredWorkareas.map(w => w.id);
        // Current visible IDs (before the mode switch)
        const currentIds: number[] = (this.form.controls.workarea_ids.value || []).filter((id: number) =>
            filteredIds.includes(id)
        );

        if (sortMode_ === 'number') {
            // Show only the manually selected set, sorted by number
            const validIds = this.manuallySelectedIds.filter(id => filteredIds.includes(id));
            const sortedIds = this.sortWorkareaIds(validIds, 'number');
            this.form.controls.workarea_ids.setValue(sortedIds, { emitEvent: false });
        } else if (sortMode_ === 'distance') {
            // Sort the current set by distance. If nothing visible, fall back to all filtered.
            const baseIds = currentIds.length > 0 ? currentIds : filteredIds;
            const sortedIds = this.sortWorkareaIds(baseIds, 'distance');
            this.form.controls.workarea_ids.setValue(sortedIds, { emitEvent: false });
            this.manuallySelectedIds = [...sortedIds];
        } else if (sortMode_ === 'input') {
            // Keep the current set as-is. If nothing visible, load all filtered.
            if (currentIds.length === 0) {
                this.form.controls.workarea_ids.setValue(filteredIds, { emitEvent: false });
                this.manuallySelectedIds = [...filteredIds];
            }
            // Otherwise keep the current order as-is
        }
    }

    onPaste(event: ClipboardEvent) {
        event.preventDefault();
        const text = event.clipboardData?.getData('text') || '';
        const rawLines = text
            .split(/[\r\n\t,;]+/)
            .map(l => l.trim())
            .filter(l => l !== '');
        const parsedNumbers = rawLines.map(l => +l).filter(n => !isNaN(n) && n > 0);
        const currentIds: number[] = [...(this.form.controls.workarea_ids.value || [])];
        parsedNumbers.forEach(num => {
            const workarea = this.availableWorkareas.find(w => w.number === num);
            if (workarea && !currentIds.includes(workarea.id)) {
                currentIds.push(workarea.id);
            }
        });
        this.form.controls.workarea_ids.setValue(currentIds);
    }

    saveAsDefault() {
        const key = this.form.controls.pdf_style_key.value;
        const customer = this.data.customer;
        if (!customer) {
            return;
        }
        this.subscriptions.add(
            this.customerService.saveDefaultPdfStyle(customer.id, key).subscribe({
                next: updatedCustomer => {
                    customer.default_pdf_style_key = updatedCustomer.default_pdf_style_key;
                },
            })
        );
    }

    get isAlreadyDefault(): boolean {
        return this.form?.controls?.pdf_style_key?.value === this.data.customer?.default_pdf_style_key;
    }

    get canDownload(): boolean {
        const workareaIds: number[] = this.form?.controls?.workarea_ids?.value ?? [];
        if (workareaIds.length > 0) {
            return true;
        }
        return this.filteredWorkareas.length > 0;
    }

    reorderSelectedWorkareas(event: CdkDragDrop<Workarea[]>) {
        const selectedIds: number[] = [...(this.form.controls.workarea_ids.value || [])];
        if (selectedIds.length < 2 || event.previousIndex === event.currentIndex) {
            return;
        }

        moveItemInArray(selectedIds, event.previousIndex, event.currentIndex);
        const sortMode = this.form.controls.workarea_sort.value;
        if (sortMode === 'number') {
            // Update manually selected and stay in number mode
            this.manuallySelectedIds = [...selectedIds];
        }
        this.form.controls.workarea_ids.setValue(selectedIds, { emitEvent: false });
        // Switch to input sort to preserve manual reordering
        if (sortMode !== 'number') {
            this.form.controls.workarea_sort.setValue('input', { emitEvent: false });
        }
    }

    searchFilteredWorkareas(term: string, item: Workarea): boolean {
        const normalizedTerm = (term || '').trim().toLowerCase();
        if (!normalizedTerm) {
            return true;
        }

        const numberText = String(item?.number ?? '').toLowerCase();
        const idText = String(item?.id ?? '').toLowerCase();
        const streetText = String(item?.street ?? '').toLowerCase();
        const houseNumberText = String(item?.housenumber ?? '').toLowerCase();
        const zipcodeText = String(item?.zipcode ?? '').toLowerCase();
        const placeText = String(item?.place ?? '').toLowerCase();

        return (
            numberText.includes(normalizedTerm) ||
            idText.includes(normalizedTerm) ||
            streetText.includes(normalizedTerm) ||
            houseNumberText.includes(normalizedTerm) ||
            zipcodeText.includes(normalizedTerm) ||
            placeText.includes(normalizedTerm)
        );
    }

    download() {
        const value = this.form.value;
        const selectedWorkareaIds = value.workarea_ids;
        // In distance/input mode, workarea_ids are already sorted via updateWorkareaIdsBySortMode
        // In number mode, workarea_ids only contains manually selected. If empty, use all filtered.
        const effectiveWorkareaIds =
            selectedWorkareaIds.length > 0 ? selectedWorkareaIds : this.filteredWorkareas.map(workarea => workarea.id);
        const selectedStyle = this.pdfStyles.find(s => s.key === value.pdf_style_key);
        this.dialog.close({
            ...value,
            workarea_ids: effectiveWorkareaIds,
            pdf_style_name: selectedStyle?.name ?? null,
        });
    }

    private sortWorkareaIds(workareaIds: number[], sortMode: 'number' | 'distance' | 'input' | string): number[] {
        switch (sortMode) {
            case 'distance':
                const workareasToSort = this.availableWorkareas.filter(
                    w => workareaIds.includes(w.id) && w.lat != null && w.lng != null
                );

                if (workareasToSort.length === 0) {
                    return workareaIds;
                }
                // Anchor on the first item of the (user-ordered) selection
                const anchorArea = [...workareasToSort].sort(
                    (a, b) => workareaIds.indexOf(a.id) - workareaIds.indexOf(b.id)
                )[0];

                // Nearest-neighbour heuristic: starting from the anchor, repeatedly walk to the closest remaining workarea
                const remainingAreas = workareasToSort.filter(w => w.id !== anchorArea.id);
                const route = [anchorArea];
                let current = anchorArea;
                while (remainingAreas.length > 0) {
                    const currentPoint = point([+current.lng, +current.lat]);
                    let nearestIndex = 0;
                    let nearestDistance = Infinity;
                    remainingAreas.forEach((w, index) => {
                        const areaPoint = point([+w.lng, +w.lat]);
                        const distance = length(shortestPath(currentPoint, areaPoint));
                        if (distance < nearestDistance) {
                            nearestDistance = distance;
                            nearestIndex = index;
                        }
                    });
                    current = remainingAreas.splice(nearestIndex, 1)[0];
                    route.push(current);
                }

                return route.map(w => w.id);
            case 'number':
                return workareaIds.sort((a, b) => {
                    const waA = this.availableWorkareas.find(w => w.id === a);
                    const waB = this.availableWorkareas.find(w => w.id === b);
                    return (waA?.number ?? 0) - (waB?.number ?? 0);
                });
            case 'input':
            default:
                return workareaIds;
        }
    }
}
